import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAbility } from '../auth/abilityContext';
import FieldProjectPicker from './FieldProjectPicker';
import { fieldCategoryPath } from './fieldCategories';
import {
    useFieldPickerOrigin,
    useRestoreFieldProject
} from './useRestoreFieldProject';

/**
 * The field tool's ONE project picker: its landing page, and where every "switch
 * project" affordance leads (see {@link fieldPickerPath}). On the first landing
 * of an app launch it may not be seen at all — the last project is restored
 * instead (see {@link useRestoreFieldProject}).
 */
export default function FieldProjectsPage() {
    const navigate = useNavigate();
    const ability = useAbility();
    const restoring = useRestoreFieldProject();
    // Set when the user came from an open project: highlight where they were,
    // and try to put them back in the same category afterwards.
    const origin = useFieldPickerOrigin(ability);
    const category = origin?.category ?? null;

    const handlePick = useCallback(
        (projectId: string) =>
            navigate(fieldCategoryPath(ability, projectId, category)),
        [navigate, ability, category]
    );

    if (restoring) {
        // Brief, and only on a launch: drawing the picker first would flash a
        // list the user is about to be navigated away from.
        return (
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                /**
                 * A flex column with the height the layout hands down, so the
                 * picker's list can scroll inside it rather than growing the
                 * page — see FieldCardList.
                 */
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}
        >
            {/* Reached from a project, the heading names the errand; the way
                back out lives in the app bar (see FieldLayout). */}
            <Typography variant="h5" noWrap>
                <FormattedMessage
                    id={
                        origin
                            ? 'field.nav.switchProject'
                            : 'field.projects.title'
                    }
                />
            </Typography>
            <FieldProjectPicker
                onPick={handlePick}
                activeProjectId={origin?.projectId}
            />
        </Box>
    );
}
